import React, { useEffect, useState } from "react";
import EmployeePerformanceTable from "./components/employeePerTable";
import { Box, Pagination, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import EmployeePerformCard from "./components/employeePerformCard";
import { useDispatch, useSelector } from "react-redux";
import { fetchEmployeePerformanceOverview } from "../../store/reducers/employeePerformanceOverview";
import { Link } from "react-router-dom";

const scoringRulesPath = "/badges";
const currentPointsNote =
  "Your points reflects your current level of learning and practice. Learning and practicing increase your score. If you're inactive for a while, your score gradually decreases as your skills and knowledge fade over time.";
const badgesEarnedNote =
  "Each badge is time-stamped and captures your score level at that moment. Every 14 days, your current points are evaluated and a new badge is awarded. Keep practicing to earn the next one.";

const columns = [
  { id: "employee", label: "Employee" },
  { id: "award_starting_date", label: "Award Starting Date" },
  { id: "readings", label: "Readings" },
  { id: "trainings", label: "Micro Trainings" },
  { id: "total_points", label: "Current points*" },
  { id: "badges", label: "Badges Earned**" },
  { id: "view", label: "View" },
];


const EmployeePerformance = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [page, setPage] = useState(1);
  const organizationId = localStorage.getItem("organisation_id");
  const dispatch = useDispatch()
  const { data, loading, error } = useSelector(
    (state) => state.employeePerformanceOverview
  );
  const itemsPerPage = 10;
  const pageCount = Math.ceil(data.length / itemsPerPage);
  const paginatedRows = data.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  useEffect(() => {
    if (organizationId) {
      dispatch(fetchEmployeePerformanceOverview(+organizationId));
    }
  }, [organizationId, dispatch]);

  const handleChangePage = (event, value) => {
    setPage(value);
  };

  return (
    <div
      style={{ marginTop: "1rem", paddingBottom: `${isMobile ? "1rem" : ""}` }}
    >
      <div
        style={{
          padding: isMobile ? "0" : "2rem",
          backgroundColor: isMobile ? "transparent" : "var(--org-bg-tint-4, #fff0f1)",
          borderRadius: isMobile ? "0" : "16px",
          minHeight: isMobile ? "" : "90vh",
        }}
      >
        <h3
          style={{
            fontWeight: "600",
            color: "#2B2B2B",
            marginBottom: isMobile ? "1rem" : "2rem",
            fontSize: isMobile ? "18px" : "24px",
          }}
        >
          Employee Performance Overview
        </h3>

        {isMobile ? (
          <div>
            {!loading && data.length > 0 && (
              <Box
                mb={2}
                sx={{
                  backgroundColor: "#fff",
                  border: "1px solid rgba(204, 204, 204, 1)",
                  borderRadius: "10px",
                  p: 2,
                  fontSize: "13px",
                  lineHeight: 1.5,
                  fontStyle: "italic",
                  fontWeight: 600,
                  boxShadow: "0px 4px 18px rgba(0, 0, 0, 0.08)",
                }}
              >
                <p>
                  <strong><em>* Current points:</em></strong> {currentPointsNote}
                </p>
                <p>
                  <strong><em>** Badges Earned:</em></strong> {badgesEarnedNote}
                </p>
                <p style={{ marginBottom: 0 }}>
                  <Link
                    to={scoringRulesPath}
                    style={{
                      color: "var(--org-bg-color, #f6615f)",
                      fontStyle: "italic",
                      fontWeight: 700,
                    }}
                  >
                    Read more about scoring rules here
                  </Link>
                </p>
              </Box>
            )}

            <Grid container spacing={1}>
              {paginatedRows.map((employee, index) => (
                <Grid item xs={12} key={employee.id}>
                  {loading ? <Box
                    height="64vh"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <div className="spinner-border text-danger" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                  </Box> : <EmployeePerformCard
                    data={employee}
                    index={(page - 1) * itemsPerPage + index}
                    loading={loading}
                    onClick={() => window.location.href = `/employee-progress/${employee.id}`}
                  />}
                </Grid>
              ))}
            </Grid>

            {data.length > 10 && (
              <Box mt={2} display="flex" justifyContent="center">
                <Pagination
                  count={pageCount}
                  page={page}
                  onChange={handleChangePage}
                  shape="rounded"
                  sx={{
                    "& .MuiPaginationItem-root": {
                      backgroundColor: "#fff",
                      borderRadius: "8px",
                    },
                    "& .Mui-selected": {
                      backgroundColor: "var(--org-bg-color, #f6615f) !important",
                      color: "#fff !important",
                      "&:hover": {
                        backgroundColor: "var(--org-bg-color, #f6615f) !important",
                      },
                    },
                  }}
                />
              </Box>
            )}
          </div>
        ) : (
          <>
            {!loading && data.length > 0 && (
              <Box
                mb={2}
                sx={{
                  backgroundColor: "#fff",
                  border: "1px solid rgba(204, 204, 204, 1)",
                  borderRadius: "10px",
                  p: 3,
                  fontSize: "14px",
                  lineHeight: 1.5,
                  fontStyle: "italic",
                  fontWeight: 600,
                  boxShadow: "0px 4px 18px rgba(0, 0, 0, 0.08)",
                }}
              >
                <Typography fontSize={14} mb={0.75}>
                  <strong><em>* Current points:</em></strong> {currentPointsNote}
                </Typography>
                <Typography fontSize={14} mb={0.75}>
                  <strong><em>** Badges Earned:</em></strong> {badgesEarnedNote}
                </Typography>
                <Typography fontSize={14}>
                  <Link
                    to={scoringRulesPath}
                    style={{
                      color: "var(--org-bg-color, #f6615f)",
                      fontStyle: "italic",
                      fontWeight: 700,
                    }}
                  >
                    Read more about scoring rules here
                  </Link>
                </Typography>
              </Box>
            )}
            <EmployeePerformanceTable columns={columns} rows={data} loading={loading} />
          </>
        )}
      </div>
    </div>
  );
};
export default EmployeePerformance;
